import React from "react"
import PropTypes from "prop-types"
import Map from "components/elements/map"
import { googleMapURL } from "utils/constants"
import { FaxIcon, PhoneIcon, PinIcon } from "./infoIcons"
import "./clinicInfo.scss"

// The hours live in one rich-text field that holds several headed groups
// ("Center Hours", "Phone Operator Hours"). Split it into groups so each
// can sit in its own column, the way the design lays them out.
const getHourGroups = sidebarDetails => {
  const nodes = sidebarDetails?.json?.content || []
  const groups = []

  nodes.forEach(node => {
    const text = (node.content || [])
      .map(child => child.value || "")
      .join("")
      .trim()

    if (!text) return

    if (node.nodeType.startsWith("heading")) {
      groups.push({ title: text, lines: [] })
    } else if (groups.length) {
      groups[groups.length - 1].lines.push(text)
    } else {
      groups.push({ title: "", lines: [text] })
    }
  })

  return groups
}

/**
 * Every centre keeps the same phone operator hours, but a few Contentful
 * entries never had the group filled in (Scottsdale, East Mesa, The Woodlands),
 * which left a visible hole where the third column belongs. Supply the standard
 * hours when an entry is missing them; an entry that has its own always wins.
 */
const OPERATOR_HOURS = {
  lines: ["M-F: 8:00 am - 5:00 pm", "Sat-Sun: Closed"],
  title: "Phone Operator Hours",
}

const withOperatorHours = groups =>
  groups.some(group => /operator/i.test(group.title))
    ? groups
    : [...groups, OPERATOR_HOURS]

const ClinicInfo = ({ clinic }) => {
  const { address, detailedAddress, enableDirectionsCTA, fax, phone } = clinic
  const hourGroups = withOperatorHours(getHourGroups(clinic.sidebarDetails))
  const hasCoords = address && address.lat != null && address.lon != null

  return (
    <section className="ClinicInfo">
      <div className="container-v2">
        <div className="ClinicInfo__card">
          <div className="ClinicInfo__body">
            <div className="ClinicInfo__col">
              <h2 className="ClinicInfo__heading">Center Info</h2>

              {detailedAddress && (
                <p className="ClinicInfo__row">
                  <PinIcon />
                  <span>{detailedAddress}</span>
                </p>
              )}

              {phone && (
                <p className="ClinicInfo__row">
                  <PhoneIcon />
                  <span>
                    <strong className="ClinicInfo__label">Phone:</strong>{" "}
                    <a className="ClinicInfo__link" href={`tel:${phone}`}>
                      {phone}
                    </a>
                  </span>
                </p>
              )}

              {fax && (
                <p className="ClinicInfo__row">
                  <FaxIcon />
                  <span>
                    <strong className="ClinicInfo__label">Fax:</strong> {fax}
                  </span>
                </p>
              )}

              {enableDirectionsCTA && hasCoords && (
                <a
                  className="ClinicInfo__link ClinicInfo__directions"
                  href={`https://www.google.com/maps/search/?api=1&query=${address.lat},${address.lon}`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Get Directions
                </a>
              )}
            </div>

            {hourGroups.map(group => (
              <div
                className="ClinicInfo__col"
                key={group.title || group.lines[0]}
              >
                {group.title && (
                  <h3 className="ClinicInfo__subheading">{group.title}</h3>
                )}
                {group.lines.map(line => (
                  <p className="ClinicInfo__hours" key={line}>
                    {line}
                  </p>
                ))}
              </div>
            ))}
          </div>

          {hasCoords && (
            <div className="ClinicInfo__map">
              <Map
                containerElement={<div className="ClinicInfo__map-container" />}
                googleMapURL={googleMapURL}
                isMarkerShown={true}
                loadingElement={<div style={{ height: `100%` }} />}
                mapElement={<div className="ClinicInfo__map-element" />}
                markerPosition={[
                  {
                    info: detailedAddress,
                    lat: address.lat,
                    lon: address.lon,
                  },
                ]}
                zoomChange={() => {}}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

ClinicInfo.propTypes = {
  clinic: PropTypes.shape({
    address: PropTypes.shape({
      lat: PropTypes.number,
      lon: PropTypes.number,
    }),
    detailedAddress: PropTypes.string,
    enableDirectionsCTA: PropTypes.bool,
    fax: PropTypes.string,
    phone: PropTypes.string,
    sidebarDetails: PropTypes.shape({ json: PropTypes.object }),
  }),
}

export default ClinicInfo
